// Castintech dashboard — Mülkler (Managed Properties) view modülü.
// Faz 46.E — castagent altında yönetilen dijital mülkler (marketing siteleri,
// internal property'ler) için kart liste + detay drawer.
//
// Veri kaynağı: vault/products/<slug>/profile.md (+ site-state, link-audit,
// theme-system, issues/) — dashboard runtime DB değil.
//
// Backend: GET /castintech-api/properties/all (Faz 46.E)
// Backend: GET /castintech-api/properties/:slug/detail (Faz 46.E)
//
// State erişimi: state.js direct import.
// Utils: utils.js (escHtml/escAttr/$).
// Api: apiFetch (api.js merkezi error boundary).
// Markdown: lib/md.js (renderMd) — drawer'da raw_* alanları için.

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, escAttr, $ } from '../utils.js';
import { renderMd } from '../lib/md.js';
import type {
  ManagedProperty,
  PropertyDetail,
  PropertiesAllResponse,
  PropertyDetailResponse,
} from '../../types/api';

// ── State (state.js'e eklenmedi; bu module-local cache) ────────────────

let cachedProperties: ManagedProperty[] = [];
let cachedDetails: Record<string, PropertyDetail> = {};
let drawerOpenSlug: string | null = null;

const CATEGORY_LABELS: Record<string, string> = {
  'marketing-site': 'Marketing Sitesi',
  'saas-platform': 'SaaS Platform',
  'service-brand': 'Hizmet Markası',
  'tool-utility': 'Araç / Yardımcı',
};

const STATUS_LABELS: Record<string, string> = {
  prod: 'Canlı',
  beta: 'Beta',
  active: 'Aktif',
  onboarding: 'Onboarding',
  alpha: 'Alpha',
};

const STATUS_DOT: Record<string, string> = {
  prod: '🟢',
  active: '🟢',
  beta: '🟡',
  onboarding: '🔵',
  alpha: '⚪',
};

// ── Public API — main.ts buradan çağırır ────────────────────────────────

export async function loadMulkler(): Promise<void> {
  try {
    const r = await apiFetch('/castintech-api/properties/all');
    if (!r.ok) {
      cachedProperties = [];
      if (state.currentPage === 'mulkler') renderMulkler();
      return;
    }
    const data = (await r.json()) as PropertiesAllResponse;
    cachedProperties = data.properties || [];
    if (state.currentPage === 'mulkler') renderMulkler();
  } catch (e) {
    console.error('[mulkler] loadMulkler error', e);
    cachedProperties = [];
    if (state.currentPage === 'mulkler') renderMulkler();
  }
}

export function renderMulkler(): void {
  const container = $('view-mulkler');
  if (!container) return;

  const subEl = $('mulkler-sub');
  if (subEl) {
    subEl.textContent = `${cachedProperties.length} mülk · vault/products/`;
  }

  const list = $('mulkler-list');
  if (!list) return;

  if (cachedProperties.length === 0) {
    list.innerHTML =
      '<div class="empty-state">Henüz yönetilen mülk yok. <code>vault/products/&lt;slug&gt;/profile.md</code> ekleyin.</div>';
    return;
  }

  list.innerHTML = cachedProperties.map(propertyCard).join('');

  // Drawer açıksa rerender
  if (drawerOpenSlug) {
    renderDrawer(drawerOpenSlug);
  }
}

// ── Card render ─────────────────────────────────────────────────────────

function propertyCard(p: ManagedProperty): string {
  const dot = STATUS_DOT[p.status || ''] || '⚪';
  const statusLabel = STATUS_LABELS[p.status || ''] || p.status || '—';
  const categoryLabel = CATEGORY_LABELS[p.category || ''] || p.category || '—';
  const ownerAgent = p.owner_agent ? `<span class="m-pill">@${escHtml(p.owner_agent)}</span>` : '';
  const parentCompany = p.parent_company ? `<span class="m-pill m-pill-muted">${escHtml(p.parent_company)}</span>` : '';

  // Documentation completeness
  const completeness: string[] = [];
  if (p.has_site_state) completeness.push('site-state');
  if (p.has_link_audit) completeness.push('link-audit');
  if (p.has_theme_system) completeness.push('theme-system');
  if (p.has_issues) completeness.push('issues');
  const completenessHtml = completeness.length
    ? `<div class="m-doc-status">📄 ${completeness.map(escHtml).join(' · ')}</div>`
    : '<div class="m-doc-status m-warn">⚠ Dokümantasyon eksik</div>';

  return `
    <div class="m-card" data-slug="${escAttr(p.slug)}" onclick="window.openMulklerDrawer('${escAttr(p.slug)}')">
      <div class="m-card-head">
        <div class="m-title">${dot} ${escHtml(p.product_name || p.slug)}</div>
        <div class="m-status-badge m-status-${escAttr(p.status || 'unknown')}">${escHtml(statusLabel)}</div>
      </div>
      <div class="m-meta">
        <span class="m-pill m-pill-cat">${escHtml(categoryLabel)}</span>
        ${ownerAgent}
        ${parentCompany}
      </div>
      <div class="m-info">
        <div class="m-info-row"><span class="m-label">Slug:</span> <code>${escHtml(p.slug)}</code></div>
        ${p.onboarded ? `<div class="m-info-row"><span class="m-label">Onboarded:</span> ${escHtml(p.onboarded)}</div>` : ''}
        ${p.updated ? `<div class="m-info-row"><span class="m-label">Updated:</span> ${escHtml(p.updated)}</div>` : ''}
      </div>
      ${completenessHtml}
      <div class="m-card-foot">
        <button class="m-btn-detail">Detay →</button>
      </div>
    </div>
  `;
}

// ── Drawer (detay) ──────────────────────────────────────────────────────

export async function openDrawer(slug: string): Promise<void> {
  drawerOpenSlug = slug;
  ensureDrawerDom();
  renderDrawer(slug);

  // Detay yoksa fetch et
  if (!cachedDetails[slug]) {
    await fetchPropertyDetail(slug);
  }
}

export function closeDrawer(): void {
  drawerOpenSlug = null;
  const el = document.getElementById('mulkler-drawer-overlay');
  if (el) el.remove();
}

function ensureDrawerDom(): void {
  if (document.getElementById('mulkler-drawer-overlay')) return;
  const overlay = document.createElement('div');
  overlay.id = 'mulkler-drawer-overlay';
  overlay.className = 'm-drawer-overlay';
  overlay.innerHTML = `
    <div class="m-drawer-panel" id="mulkler-drawer-panel">
      <div class="m-drawer-loading">Yükleniyor…</div>
    </div>
  `;
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeDrawer();
  });
  document.body.appendChild(overlay);
}

async function fetchPropertyDetail(slug: string): Promise<void> {
  try {
    const r = await apiFetch(`/castintech-api/properties/${encodeURIComponent(slug)}/detail`);
    if (!r.ok) {
      console.error('[mulkler] detail fetch failed', r.status);
      return;
    }
    const data = (await r.json()) as PropertyDetailResponse;
    cachedDetails[slug] = data.property;
    if (drawerOpenSlug === slug) renderDrawer(slug);
  } catch (e) {
    console.error('[mulkler] fetchPropertyDetail error', e);
  }
}

function renderDrawer(slug: string): void {
  const panel = document.getElementById('mulkler-drawer-panel');
  if (!panel) return;
  const detail = cachedDetails[slug];
  if (!detail) {
    panel.innerHTML = '<div class="m-drawer-loading">Yükleniyor…</div>';
    return;
  }

  const issuesHtml = detail.issues.length
    ? detail.issues.map(
        (i) => `
          <div class="m-issue-row">
            <span class="m-issue-status m-issue-${escAttr(i.status)}">${escHtml(i.status)}</span>
            ${i.priority ? `<span class="m-issue-priority">${escHtml(i.priority)}</span>` : ''}
            <span class="m-issue-title">${escHtml(i.title)}</span>
          </div>
        `
      ).join('')
    : '<div class="m-empty-sub">İssue dosyası yok.</div>';

  panel.innerHTML = `
    <div class="m-drawer-header">
      <div class="m-drawer-title">${escHtml(detail.product_name || detail.slug)}</div>
      <button class="m-drawer-close" onclick="window.closeMulklerDrawer()">✕</button>
    </div>

    <div class="m-drawer-tabs">
      <button class="m-tab active" data-tab="genel" onclick="window.mulklerSwitchTab(this, 'genel')">Genel</button>
      <button class="m-tab" data-tab="site-state" onclick="window.mulklerSwitchTab(this, 'site-state')">Site Durumu</button>
      <button class="m-tab" data-tab="link-audit" onclick="window.mulklerSwitchTab(this, 'link-audit')">Link Audit</button>
      <button class="m-tab" data-tab="theme-system" onclick="window.mulklerSwitchTab(this, 'theme-system')">Tema</button>
      <button class="m-tab" data-tab="issues" onclick="window.mulklerSwitchTab(this, 'issues')">İssue</button>
    </div>

    <div class="m-drawer-body">
      <div class="m-tab-pane active" data-pane="genel">
        <div class="m-info-grid">
          <div><strong>Slug:</strong> <code>${escHtml(detail.slug)}</code></div>
          <div><strong>Kategori:</strong> ${escHtml(CATEGORY_LABELS[detail.category || ''] || detail.category || '—')}</div>
          <div><strong>Statü:</strong> ${escHtml(STATUS_LABELS[detail.status || ''] || detail.status || '—')}</div>
          <div><strong>Owner Agent:</strong> ${detail.owner_agent ? '@' + escHtml(detail.owner_agent) : '—'}</div>
          <div><strong>Parent:</strong> ${escHtml(detail.parent_company || '—')}</div>
          <div><strong>Company ID:</strong> ${escHtml(detail.company_id || 'TBD')}</div>
          <div><strong>Launch:</strong> ${escHtml(detail.launch_date || '—')}</div>
          <div><strong>Onboarded:</strong> ${escHtml(detail.onboarded || '—')}</div>
          <div><strong>Updated:</strong> ${escHtml(detail.updated || '—')}</div>
        </div>
        <h4>Profile (vault/products/${escHtml(detail.slug)}/profile.md)</h4>
        <div class="m-md-body">${renderMd(stripFrontmatter(detail.raw_profile))}</div>
      </div>

      <div class="m-tab-pane" data-pane="site-state" style="display:none">
        ${detail.raw_site_state
          ? `<div class="m-md-body">${renderMd(stripFrontmatter(detail.raw_site_state))}</div>`
          : '<div class="m-empty-sub">site-state.md yok.</div>'}
      </div>

      <div class="m-tab-pane" data-pane="link-audit" style="display:none">
        ${detail.raw_link_audit
          ? `<div class="m-md-body">${renderMd(stripFrontmatter(detail.raw_link_audit))}</div>`
          : '<div class="m-empty-sub">link-audit.md yok.</div>'}
      </div>

      <div class="m-tab-pane" data-pane="theme-system" style="display:none">
        ${detail.raw_theme_system
          ? `<div class="m-md-body">${renderMd(stripFrontmatter(detail.raw_theme_system))}</div>`
          : '<div class="m-empty-sub">theme-system.md yok.</div>'}
      </div>

      <div class="m-tab-pane" data-pane="issues" style="display:none">
        <h4>vault/products/${escHtml(detail.slug)}/issues/</h4>
        ${issuesHtml}
      </div>
    </div>
  `;
}

function stripFrontmatter(content: string): string {
  return content.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, '');
}

// ── Window bridge — HTML onclick handler'ları için ──────────────────────

(window as unknown as Record<string, unknown>).openMulklerDrawer = (slug: string) => openDrawer(slug);
(window as unknown as Record<string, unknown>).closeMulklerDrawer = () => closeDrawer();
(window as unknown as Record<string, unknown>).mulklerSwitchTab = (btn: HTMLElement, tab: string) => {
  const panel = document.getElementById('mulkler-drawer-panel');
  if (!panel) return;
  panel.querySelectorAll('.m-tab').forEach((b) => b.classList.remove('active'));
  btn.classList.add('active');
  panel.querySelectorAll<HTMLElement>('.m-tab-pane').forEach((p) => {
    p.style.display = p.getAttribute('data-pane') === tab ? '' : 'none';
  });
};
